/**
 * 全局错误处理器
 * 统一处理和上报错误
 */
import { AppError, isAppError } from './index.js'
import { SDK_ERRORS } from './codes.js'
import { error as logError, warn } from '@/utils/logger.js'
import { eventBus, SDK_EVENTS } from '@/sdk/eventBus.js'

// 错误处理配置
const config = {
  // 是否在控制台输出
  consoleEnabled: true,
  // 是否上报到服务器
  reportEnabled: false,
  // 报告地址
  reportUrl: '',
  // 是否显示 Toast
  toastEnabled: true,
  // 静默处理的错误码（不显示 Toast）
  silentCodes: []
}

// 全局错误处理器列表
const globalHandlers = []

/**
 * 配置错误处理器
 */
export function configure(options) {
  Object.assign(config, options)
}

/**
 * 注册全局错误处理器
 * @param {Function} handler - (error, handled) => void
 * @returns {Function} 取消注册函数
 */
export function onError(handler) {
  globalHandlers.push(handler)
  return () => {
    const index = globalHandlers.indexOf(handler)
    if (index > -1) globalHandlers.splice(index, 1)
  }
}

/**
 * 统一错误处理入口
 * @param {Error|AppError|string} error
 * @param {Object} context - 错误上下文
 * @returns {AppError}
 */
export function handleError(error, context = {}) {
  let appError

  // 转换为 AppError
  if (isAppError(error)) {
    appError = error
  } else if (error instanceof Error) {
    appError = new AppError({
      code: 'SYS_UNKNOWN',
      message: error.message,
      originalError: error,
      show: true
    })
  } else {
    appError = new AppError({
      code: 'SYS_UNKNOWN',
      message: String(error),
      show: true
    })
  }

  // 添加上下文
  if (context) {
    appError.details = { ...appError.details, ...context }
  }

  // 日志输出
  if (config.consoleEnabled) {
    const logger = getLogger()
    logger.error(appError)
  }

  // 触发事件
  eventBus.emit(SDK_EVENTS.TIM_ERROR, appError)

  // 调用全局处理器
  globalHandlers.forEach(handler => {
    try {
      handler(appError)
    } catch (e) {
      console.error('[ErrorHandler] Handler error:', e)
    }
  })

  return appError
}

/**
 * 获取对应模块的日志记录器
 */
function getLogger() {
  return {
    error: (...args) => logError('ErrorHandler', ...args),
    warn: (...args) => warn('ErrorHandler', ...args)
  }
}

/**
 * 包装 SDK 错误为 AppError
 * @param {Object} errorDef - 错误定义 {code, message}
 * @param {Error} originalError - 原始错误
 * @param {string} message - 自定义错误消息
 * @returns {AppError}
 */
export function wrapSDKError(errorDef, originalError, message) {
  return new AppError({
    code: errorDef.code,
    message: message || errorDef.message,
    source: 'SDK',
    originalError,
    show: !config.silentCodes.includes(errorDef.code)
  })
}

/**
 * 处理 SDK 错误
 * @param {Error} originalError - 原始错误
 * @param {Object} errorDef - 错误定义 {code, message}
 * @param {Object} context - 额外上下文
 */
export function handleSDKError(originalError, errorDef, context = {}) {
  const appError = new AppError({
    code: errorDef.code,
    message: errorDef.message,
    source: 'SDK',
    originalError,
    show: !config.silentCodes.includes(errorDef.code),
    details: context
  })

  return handleError(appError, context)
}

/**
 * 处理网络错误
 */
export function handleNetworkError(originalError, context = {}) {
  const isTimeout = originalError?.message?.includes('timeout')
  const isOffline = !navigator.onLine

  return handleError(new AppError({
    code: isTimeout ? 'NET_002' : (isOffline ? 'NET_003' : 'NET_001'),
    message: isTimeout ? '请求超时' : (isOffline ? '网络连接已断开' : '网络请求失败'),
    source: 'NETWORK',
    originalError,
    show: true,
    details: context
  }), context)
}

/**
 * 处理业务错误
 */
export function handleBusinessError(errorDef, message, context = {}) {
  return handleError(new AppError({
    code: errorDef.code,
    message: message || errorDef.message,
    source: 'BUSINESS',
    show: true,
    details: context
  }), context)
}

/**
 * 安全执行（包装 Promise）
 * @param {Function} fn
 * @param {Object} errorContext - 错误上下文
 * @returns {Promise}
 */
export function safeExecute(fn, errorContext = {}) {
  return fn().catch(error => {
    handleError(error, errorContext)
    return null
  })
}

/**
 * 异步包装器 - 自动处理错误
 */
export function withErrorHandler(fn, errorDef, contextExtractor = null) {
  return async (...args) => {
    try {
      return await fn(...args)
    } catch (error) {
      const context = contextExtractor ? contextExtractor(...args) : {}
      if (errorDef) {
        handleSDKError(error, errorDef, context)
      } else {
        handleError(error, context)
      }
      throw error
    }
  }
}

/**
 * Vue 错误处理器（用于 Vue.config.errorHandler）
 */
export function vueErrorHandler(error, instance, info) {
  const context = {
    component: instance?.$options?.name || 'Unknown',
    info
  }

  handleError(error, context)
}

/**
 * 全局未处理 Promise 错误
 */
export function setupGlobalErrorHandlers() {
  // Vue 错误
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason
      if (error && typeof error === 'object') {
        event.preventDefault()
        handleError(error, { type: 'unhandledrejection' })
      }
    })

    // 普通错误
    window.addEventListener('error', (event) => {
      if (event.error) {
        handleError(event.error, { type: 'window.onerror' })
      }
    })
  }
}

export default {
  configure,
  onError,
  handleError,
  wrapSDKError,
  handleSDKError,
  handleNetworkError,
  handleBusinessError,
  safeExecute,
  withErrorHandler,
  vueErrorHandler,
  setupGlobalErrorHandlers
}
